import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  DollarSign,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  Filter,
  RotateCcw,
  Calendar,
  MapPin,
  ListOrdered,
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import { Input } from "../../components/ui/input";
import CommonTable, {
  type TableColumn,
} from "../../components/common/CommonTable";
import TablePagination from "../../components/common/TablePagination";
import moment from "moment";

// Tab 1: Pay Cycle Schedule (Matches the user's uploaded mock/image)
const mockPayCycles = [
  {
    id: 1,
    site: "Site 1 – Foundation",
    name: "Ramesh Kumar",
    nextPayDate: "2026-08-25",
    amount: 145000,
    status: "Upcoming",
  },
  {
    id: 2,
    site: "Site 2 – Block A",
    name: "Sunita Devi",
    nextPayDate: "2026-08-26",
    amount: 98000,
    status: "Upcoming",
  },
  {
    id: 3,
    site: "Site 4 – Roofing",
    name: "Arvind Yadav",
    nextPayDate: "2026-08-27",
    amount: 210000,
    status: "Upcoming",
  },
  {
    id: 4,
    site: "Site 3 – Interior",
    name: "Sanjeev Kumar",
    nextPayDate: "2026-08-24",
    amount: 76000,
    status: "Today",
  },
  {
    id: 5,
    site: "Site 4 – Roofing",
    name: "Vikram Singh",
    nextPayDate: "2026-08-23",
    amount: 35000,
    status: "Overdue",
  },
  {
    id: 6,
    site: "Site 2 – Block A",
    name: "Anita Patel",
    nextPayDate: "2026-09-08",
    amount: 125000,
    status: "Upcoming",
  },
];

// Tab 2: Payments History (User's original mock data)
const mockPayments = [
  {
    id: "#P001",
    employee: "Ramesh Kumar",
    amount: 18500,
    date: "2026-08-15",
    mode: "Bank Transfer",
    status: "Completed",
  },
  {
    id: "#P002",
    employee: "Sunita Devi",
    amount: 12000,
    date: "2026-08-15",
    mode: "Cash",
    status: "Completed",
  },
  {
    id: "#P003",
    employee: "Arvind Yadav",
    amount: 22000,
    date: "2026-08-16",
    mode: "Bank Transfer",
    status: "Pending",
  },
  {
    id: "#P004",
    employee: "Priya Sharma",
    amount: 28000,
    date: "2026-08-16",
    mode: "Cheque",
    status: "Completed",
  },
  {
    id: "#P005",
    employee: "Vikram Singh",
    amount: 11000,
    date: "2026-08-17",
    mode: "Cash",
    status: "Failed",
  },
  {
    id: "#P006",
    employee: "Anita Patel",
    amount: 19500,
    date: "2026-08-17",
    mode: "Bank Transfer",
    status: "Completed",
  },
  {
    id: "#P007",
    employee: "Ramesh Kumar",
    amount: 5000,
    date: "2026-08-20",
    mode: "Cash",
    status: "Pending",
  },
];

interface IPageInfo {
  currentPage: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

const PaymentReport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"schedule" | "history">(
    "schedule",
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSite, setSelectedSite] = useState("All");

  // Get active site filters list
  const uniqueSites = useMemo(() => {
    const list =
      activeTab === "schedule"
        ? mockPayCycles.map((p) => p.site)
        : mockPayments.map((p) => {
            // Fallback to match sites for history tab items
            const matchingCycle = mockPayCycles.find(
              (c) => c.name === p.employee,
            );
            return matchingCycle ? matchingCycle.site : "Site 1 – Foundation";
          });
    return ["All", ...Array.from(new Set(list))];
  }, [activeTab]);

  // Pagination states
  const [schedulePagination, setSchedulePagination] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 5,
    total: mockPayCycles.length,
    totalPages: Math.ceil(mockPayCycles.length / 5),
  });

  const [historyPagination, setHistoryPagination] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 5,
    total: mockPayments.length,
    totalPages: Math.ceil(mockPayments.length / 5),
  });

  // Reset page when tab changes
  useEffect(() => {
    setSearchTerm("");
    setSelectedSite("All");
  }, [activeTab]);

  // Dynamic filter for schedule tab
  const filteredScheduleData = useMemo(() => {
    return mockPayCycles.filter((item) => {
      const matchesSearch =
        !searchTerm.trim() ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.site.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSite = selectedSite === "All" || item.site === selectedSite;
      return matchesSearch && matchesSite;
    });
  }, [searchTerm, selectedSite]);

  // Dynamic filter for history tab
  const filteredHistoryData = useMemo(() => {
    return mockPayments.filter((item) => {
      const matchesSearch =
        !searchTerm.trim() ||
        item.employee.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.id.toLowerCase().includes(searchTerm.toLowerCase());

      const matchingCycle = mockPayCycles.find((c) => c.name === item.employee);
      const itemSite = matchingCycle
        ? matchingCycle.site
        : "Site 1 – Foundation";
      const matchesSite = selectedSite === "All" || itemSite === selectedSite;

      return matchesSearch && matchesSite;
    });
  }, [searchTerm, selectedSite]);

  // Sync Schedule Pagination
  useEffect(() => {
    const total = filteredScheduleData.length;
    const totalPages = Math.ceil(total / schedulePagination.pageSize) || 1;
    setSchedulePagination((prev) => ({
      ...prev,
      total,
      totalPages,
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage,
    }));
  }, [filteredScheduleData.length, schedulePagination.pageSize]);

  // Sync History Pagination
  useEffect(() => {
    const total = filteredHistoryData.length;
    const totalPages = Math.ceil(total / historyPagination.pageSize) || 1;
    setHistoryPagination((prev) => ({
      ...prev,
      total,
      totalPages,
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage,
    }));
  }, [filteredHistoryData.length, historyPagination.pageSize]);

  // Sliced Data
  const paginatedScheduleData = useMemo(() => {
    const startIndex =
      (schedulePagination.currentPage - 1) * schedulePagination.pageSize;
    return filteredScheduleData.slice(
      startIndex,
      startIndex + schedulePagination.pageSize,
    );
  }, [
    filteredScheduleData,
    schedulePagination.currentPage,
    schedulePagination.pageSize,
  ]);

  const paginatedHistoryData = useMemo(() => {
    const startIndex =
      (historyPagination.currentPage - 1) * historyPagination.pageSize;
    return filteredHistoryData.slice(
      startIndex,
      startIndex + historyPagination.pageSize,
    );
  }, [
    filteredHistoryData,
    historyPagination.currentPage,
    historyPagination.pageSize,
  ]);

  // Stats Calculations
  const stats = useMemo(() => {
    const disbursed = mockPayments
      .filter((p) => p.status === "Completed")
      .reduce((sum, p) => sum + p.amount, 0);
    const pendingHistory = mockPayments
      .filter((p) => p.status === "Pending")
      .reduce((sum, p) => sum + p.amount, 0);
    const upcomingSchedule = mockPayCycles
      .filter((c) => c.status === "Upcoming" || c.status === "Today")
      .reduce((sum, c) => sum + c.amount, 0);
    const overdueSchedule = mockPayCycles
      .filter((c) => c.status === "Overdue")
      .reduce((sum, c) => sum + c.amount, 0);

    return {
      disbursed,
      pendingHistory,
      upcomingSchedule,
      overdueSchedule,
      completedCount: mockPayments.filter((p) => p.status === "Completed")
        .length,
      scheduleCount: mockPayCycles.length,
    };
  }, []);

  // CSV Export
  const handleExportCSV = () => {
    let headers: string[] = [];
    let rows: any[][] = [];
    let fileName = "";

    if (activeTab === "schedule") {
      headers = [
        "S.No",
        "Site",
        "Labor Name",
        "Next Pay Date",
        "Amount (₹)",
        "Status",
      ];
      rows = filteredScheduleData.map((item, index) => [
        index + 1,
        item.site,
        item.name,
        moment(item.nextPayDate).format("DD MMM YYYY"),
        item.amount,
        item.status,
      ]);
      fileName = `Pay_Cycle_Schedule_${moment().format("DD_MM_YYYY")}.csv`;
    } else {
      headers = [
        "Transaction ID",
        "Employee",
        "Amount (₹)",
        "Date",
        "Mode",
        "Status",
      ];
      rows = filteredHistoryData.map((item) => [
        item.id,
        item.employee,
        item.amount,
        moment(item.date).format("DD-MM-YYYY"),
        item.mode,
        item.status,
      ]);
      fileName = `Payment_History_${moment().format("DD_MM_YYYY")}.csv`;
    }

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [
        headers.join(","),
        ...rows.map((e) => e.map((val) => `"${val}"`).join(",")),
      ].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Schedule Table Columns (Matches image layout style)
  const scheduleColumns: TableColumn<(typeof mockPayCycles)[0]>[] = [
    {
      key: "id",
      header: "S.No",
      render: (item, idx) => (
        <span className="font-mono text-slate-500 text-sm font-semibold pl-1">
          {(schedulePagination.currentPage - 1) * schedulePagination.pageSize +
            (idx ?? 0) +
            1}
        </span>
      ),
    },
    {
      key: "site",
      header: "Site",
      render: (item) => (
        <div className="flex items-center gap-1.5 text-slate-600">
          <MapPin size={14} className="text-emerald-500 shrink-0" />
          <span className="text-sm font-medium">{item.site}</span>
        </div>
      ),
    },
    {
      key: "name",
      header: "Labor Name",
      render: (item) => {
        const initials = item.name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .substring(0, 2)
          .toUpperCase();
        return (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 font-bold text-xs flex items-center justify-center shadow-sm shrink-0">
              {initials}
            </div>
            <span className="font-semibold text-slate-800 text-sm">
              {item.name}
            </span>
          </div>
        );
      },
    },
    {
      key: "nextPayDate",
      header: "Next Pay Date",
      render: (item) => (
        <span className="font-medium text-slate-700 text-sm">
          {moment(item.nextPayDate).format("DD MMM YYYY")}
        </span>
      ),
    },
    {
      key: "id", // Using custom key for Days Left display logic
      header: "Days Left",
      render: (item) => {
        const today = moment("2026-08-24"); // Current date matching metadata
        const payDate = moment(item.nextPayDate);
        const diffDays = payDate.diff(today, "days");

        if (diffDays === 0) {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded bg-rose-50 text-rose-600 border border-rose-100">
              Today
            </span>
          );
        } else if (diffDays === 1) {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded bg-orange-50 text-orange-600 border border-orange-100">
              1 day left
            </span>
          );
        } else if (diffDays > 1) {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded bg-blue-50 text-blue-600 border border-blue-100">
              {diffDays} days left
            </span>
          );
        } else {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded bg-slate-100 text-slate-500 border border-slate-200">
              {Math.abs(diffDays)} days overdue
            </span>
          );
        }
      },
    },
    {
      key: "amount",
      header: "Amount",
      render: (item) => (
        <span className="font-bold text-slate-900 text-sm">
          ₹{item.amount.toLocaleString()}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => {
        const statusColors: Record<string, string> = {
          Upcoming: "bg-blue-50 text-blue-600 border border-blue-100",
          Today: "bg-rose-50 text-rose-600 border border-rose-100",
          Overdue: "bg-amber-50 text-amber-600 border border-amber-100",
        };
        const style = statusColors[item.status] || "bg-slate-50 text-slate-600";
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 text-xs font-bold rounded-full border ${style}`}
          >
            {item.status}
          </span>
        );
      },
    },
  ];

  // History Table Columns
  const historyColumns: TableColumn<(typeof mockPayments)[0]>[] = [
    {
      key: "id",
      header: "Transaction ID",
      render: (item) => (
        <span className="font-mono font-bold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded text-xs">
          {item.id}
        </span>
      ),
    },
    {
      key: "employee",
      header: "Employee",
      render: (item) => {
        const initials = item.employee
          .split(" ")
          .map((n) => n[0])
          .join("")
          .substring(0, 2)
          .toUpperCase();
        return (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 border border-slate-200 font-bold text-xs flex items-center justify-center shrink-0">
              {initials}
            </div>
            <span className="font-semibold text-slate-800 text-sm">
              {item.employee}
            </span>
          </div>
        );
      },
    },
    {
      key: "amount",
      header: "Amount (₹)",
      render: (item) => (
        <span className="font-bold text-slate-900">
          ₹{item.amount.toLocaleString()}
        </span>
      ),
    },
    {
      key: "date",
      header: "Date",
      render: (item) => (
        <span className="text-slate-600 text-sm">
          {moment(item.date).format("DD-MM-YYYY")}
        </span>
      ),
    },
    {
      key: "mode",
      header: "Mode",
      render: (item) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200/55">
          {item.mode}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item) => {
        const statusColors: Record<string, string> = {
          Completed: "bg-emerald-50 text-emerald-700 border-emerald-100",
          Pending: "bg-amber-50 text-amber-700 border-amber-100",
          Failed: "bg-rose-50 text-rose-700 border-rose-100",
        };
        const style = statusColors[item.status] || "bg-slate-50 text-slate-600";
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full border ${style}`}
          >
            {item.status}
          </span>
        );
      },
    },
  ];

  const handleSchedulePageSize = (size: number) => {
    setSchedulePagination((prev) => ({
      ...prev,
      pageSize: size,
      currentPage: 1,
      totalPages: Math.ceil(filteredScheduleData.length / size),
    }));
  };

  const handleHistoryPageSize = (size: number) => {
    setHistoryPagination((prev) => ({
      ...prev,
      pageSize: size,
      currentPage: 1,
      totalPages: Math.ceil(filteredHistoryData.length / size),
    }));
  };

  const hasActiveFilters = useMemo(() => {
    return searchTerm.trim() !== "" || selectedSite !== "All";
  }, [searchTerm, selectedSite]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedSite("All");
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6">
      {/* Top Header Panel */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-5">
        <MainHeading title="Payment & Pay Cycle Dashboard" />
        <button
          onClick={handleExportCSV}
          disabled={
            activeTab === "schedule"
              ? filteredScheduleData.length === 0
              : filteredHistoryData.length === 0
          }
          className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:pointer-events-none rounded-xl shadow-sm transition-all duration-200"
        >
          <Download size={16} />
          Export {activeTab === "schedule" ? "Schedule" : "History"} CSV
        </button>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Total Disbursed */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between group">
          <div className="space-y-1">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Total Disbursed
            </p>
            <p className="text-2xl font-extrabold text-emerald-600">
              ₹{stats.disbursed.toLocaleString()}
            </p>
          </div>
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
            <DollarSign size={22} className="stroke-[2.5]" />
          </div>
        </div>

        {/* Pending payouts */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between group">
          <div className="space-y-1">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Pending (History)
            </p>
            <p className="text-2xl font-extrabold text-amber-600">
              ₹{stats.pendingHistory.toLocaleString()}
            </p>
          </div>
          <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
            <Clock size={22} className="stroke-[2.5]" />
          </div>
        </div>

        {/* Upcoming Payouts (next cycles) */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between group">
          <div className="space-y-1">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Upcoming Cycles
            </p>
            <p className="text-2xl font-extrabold text-blue-600">
              ₹{stats.upcomingSchedule.toLocaleString()}
            </p>
          </div>
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
            <Calendar size={22} className="stroke-[2.5]" />
          </div>
        </div>

        {/* Overdue Payouts */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between group">
          <div className="space-y-1">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Overdue Cycles
            </p>
            <p className="text-2xl font-extrabold text-rose-600">
              ₹{stats.overdueSchedule.toLocaleString()}
            </p>
          </div>
          <div className="p-3 bg-rose-50 text-rose-600 rounded-xl">
            <XCircle size={22} className="stroke-[2.5]" />
          </div>
        </div>
      </div>

      {/* Tab Selection Navigation */}
      <div className="flex border-b border-slate-200 mt-4">
        <button
          onClick={() => setActiveTab("schedule")}
          className={`px-5 py-3 font-bold text-sm border-b-2 transition-all duration-200 -mb-[2px] ${
            activeTab === "schedule"
              ? "border-indigo-600 text-indigo-600"
              : "border-transparent text-slate-400 hover:text-slate-600"
          }`}
        >
          Pay Cycle Schedule (Upcoming Due)
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`px-5 py-3 font-bold text-sm border-b-2 transition-all duration-200 -mb-[2px] ${
            activeTab === "history"
              ? "border-indigo-600 text-indigo-600"
              : "border-transparent text-slate-400 hover:text-slate-600"
          }`}
        >
          Payment Transactions History
        </button>
      </div>

      {/* Filter panel */}
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-5 space-y-4">
        <div className="flex items-center gap-2 text-slate-800 font-semibold border-b border-slate-100 pb-3">
          <Filter size={16} className="text-indigo-500" />
          <span>Search & Filter Payments</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Search Input */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-500">
              Search
            </label>
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 hover:border-slate-300 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100 rounded-xl px-3 py-1.5 transition-all w-full">
              <Search size={16} className="text-slate-400 shrink-0" />
              <Input
                placeholder={
                  activeTab === "schedule"
                    ? "Search by Name or Site..."
                    : "Search by Employee or Transaction ID..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-none shadow-none p-0 h-7 bg-transparent text-sm focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-slate-400 w-full"
              />
            </div>
          </div>

          {/* Site Filter */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-500">
              Filter by Site
            </label>
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 hover:border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-xl px-3 py-2 text-sm text-slate-700 outline-none transition-all"
            >
              {uniqueSites.map((site) => (
                <option key={site} value={site}>
                  {site === "All" ? "All Sites" : site}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active Filter Badges */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs font-semibold text-slate-400 mr-1">
                Active filters:
              </span>
              {searchTerm && (
                <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-xs font-medium text-slate-600">
                  Search: "{searchTerm}"
                </span>
              )}
              {selectedSite !== "All" && (
                <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-50 border border-emerald-100 text-xs font-medium text-emerald-700">
                  Site: {selectedSite}
                </span>
              )}
            </div>
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700 transition-colors px-2 py-1 hover:bg-rose-50 rounded-lg"
            >
              <RotateCcw size={12} />
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-4">
        {activeTab === "schedule" ? (
          paginatedScheduleData.length > 0 ? (
            <>
              <div className="px-1 pb-3 flex items-center justify-between border-b border-slate-100 mb-4">
                <span className="text-sm font-semibold text-slate-700">
                  Pay Cycle Schedule Grid ({filteredScheduleData.length}{" "}
                  records)
                </span>
                <span className="text-xs text-slate-400 font-medium">
                  Page {schedulePagination.currentPage} of{" "}
                  {schedulePagination.totalPages}
                </span>
              </div>
              <CommonTable
                data={paginatedScheduleData}
                columns={scheduleColumns}
              />
              <div className="mt-4 pt-4 border-t border-slate-100">
                <TablePagination
                  paginationData={schedulePagination}
                  setPaginationData={setSchedulePagination}
                  handleChangePageSize={handleSchedulePageSize}
                  isLoading={false}
                />
              </div>
            </>
          ) : (
            <div className="text-center py-16 space-y-3">
              <div className="inline-flex items-center justify-center p-3 bg-slate-50 border border-slate-100 rounded-full text-slate-400">
                <Search size={24} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800">
                  No schedules match search
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Reset filter parameters to view schedules.
                </p>
              </div>
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 text-xs font-semibold text-indigo-600 hover:text-white border border-indigo-200 hover:bg-indigo-600 rounded-xl transition-all"
              >
                Clear Filters
              </button>
            </div>
          )
        ) : paginatedHistoryData.length > 0 ? (
          <>
            <div className="px-1 pb-3 flex items-center justify-between border-b border-slate-100 mb-4">
              <span className="text-sm font-semibold text-slate-700">
                Transactions History Grid ({filteredHistoryData.length} records)
              </span>
              <span className="text-xs text-slate-400 font-medium">
                Page {historyPagination.currentPage} of{" "}
                {historyPagination.totalPages}
              </span>
            </div>
            <CommonTable data={paginatedHistoryData} columns={historyColumns} />
            <div className="mt-4 pt-4 border-t border-slate-100">
              <TablePagination
                paginationData={historyPagination}
                setPaginationData={setHistoryPagination}
                handleChangePageSize={handleHistoryPageSize}
                isLoading={false}
              />
            </div>
          </>
        ) : (
          <div className="text-center py-16 space-y-3">
            <div className="inline-flex items-center justify-center p-3 bg-slate-50 border border-slate-100 rounded-full text-slate-400">
              <Search size={24} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">
                No transactions match search
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Reset filter parameters to view transactions.
              </p>
            </div>
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 text-xs font-semibold text-indigo-600 hover:text-white border border-indigo-200 hover:bg-indigo-600 rounded-xl transition-all"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReport;
